import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, MapPin, Users, Zap, Shield, Truck, Clock, DollarSign } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const FeaturesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1600&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/80 to-blue-800/90 z-0" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-3xl md:text-5xl font-bold mb-6">
            Built for Businesses<br />
            <span className="text-green-400">That Go to the Customer</span>
          </h1>
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto text-blue-100">
            Route optimization, job tracking, invoicing, and business management—designed specifically for field service professionals.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/register" 
              className="bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-700 shadow-lg transition-colors"
            >
              Start Free 14-Day Trial
            </Link>
            <Link 
              to="/pricing" 
              className="bg-white/10 backdrop-blur text-white border border-white/30 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white/20 transition-colors"
            >
              View Pricing
            </Link>
          </div>
          <p className="mt-6 text-blue-200 text-sm">
            Plans from $19/month • No credit card required
          </p>
        </div>
      </section>

      {/* Why SuddyFlow - Quick Value Props */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div className="p-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Zap className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Simple Setup</h3>
              <p className="text-sm text-gray-600">Up and running in minutes, not days</p>
            </div>
            <div className="p-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <MapPin className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Smart Routing</h3>
              <p className="text-sm text-gray-600">Optimize routes to save time & fuel</p>
            </div>
            <div className="p-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Team Ready</h3>
              <p className="text-sm text-gray-600">Add crew without per-user fees</p>
            </div>
            <div className="p-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Tax Ready</h3>
              <p className="text-sm text-gray-600">Schedule C reports built-in</p>
            </div>
          </div>
        </div>
      </section>

      {/* SuddyRoute™ - Hero Feature */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-emerald-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center bg-green-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
                🗺️ SUDDYROUTE™
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Stop Wasting Hours<br />Planning Routes
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Our intelligent routing doesn't just find the shortest path—it optimizes for 
                <strong> revenue per hour</strong>. High-value jobs get priority while keeping 
                your route efficient.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-700">Revenue-first algorithm prioritizes high-value jobs</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-700">One-tap handoff to Google Maps or Apple Maps</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-700">Compare strategies: Revenue vs Balanced vs Distance</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-700">Week planner shows optimization opportunities</span>
                </li>
              </ul>
              <p className="text-sm text-gray-500">
                Available in Pro ($49/mo) and Enterprise ($129/mo) plans
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-green-200">
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white text-center">
                <div className="text-5xl mb-4">🗺️</div>
                <h3 className="text-xl font-bold mb-2">Route Optimized</h3>
                <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
                  <div className="bg-white/20 rounded-lg p-3">
                    <div className="font-bold text-lg">6</div>
                    <div className="text-green-100">Stops</div>
                  </div>
                  <div className="bg-white/20 rounded-lg p-3">
                    <div className="font-bold text-lg">42mi</div>
                    <div className="text-green-100">Total</div>
                  </div>
                  <div className="bg-white/20 rounded-lg p-3">
                    <div className="font-bold text-lg">$185</div>
                    <div className="text-green-100">/hour</div>
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {['Revenue Strategy', 'Balanced Strategy', 'Distance Strategy'].map((strategy, i) => (
                  <div key={strategy} className={`flex items-center justify-between p-3 rounded-lg ${i === 0 ? 'bg-green-50 border-2 border-green-400' : 'bg-gray-50'}`}>
                    <span className={`font-medium ${i === 0 ? 'text-green-700' : 'text-gray-600'}`}>{strategy}</span>
                    {i === 0 && <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full">Best</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Property Tracking Feature */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    🏠
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Johnson Residence</h4>
                    <p className="text-sm text-gray-500">2,400 sq ft • 2 stories</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-gray-500">Driveway</p>
                    <p className="font-semibold">800 sq ft</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-gray-500">Deck</p>
                    <p className="font-semibold">200 sq ft</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-gray-500">Gate Code</p>
                    <p className="font-semibold">#1234</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-gray-500">Last Service</p>
                    <p className="font-semibold">Dec 15</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-flex items-center bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
                🏠 PROPERTY TRACKING
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Know Every Property<br />Before You Arrive
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Store property details, measurements, gate codes, and service history. 
                No more asking customers the same questions twice.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-700">Square footage for accurate quotes</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-700">Gate codes and access instructions</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-700">Complete service history per property</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-700">Multi-property customers (property managers)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Plans Overview - Mobile Friendly Cards */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Plans That Grow With You
            </h2>
            <p className="text-lg text-gray-600">
              Start solo at $19/month. Scale to enterprise when ready.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Solo Plan */}
            <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-blue-400 hover:shadow-lg transition-all">
              <div className="text-center mb-6">
                <span className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold mb-3">
                  SOLO
                </span>
                <div className="text-4xl font-bold text-gray-900">$19<span className="text-lg font-normal text-gray-500">/mo</span></div>
                <p className="text-gray-600 mt-2">Perfect for getting started</p>
              </div>
              
              <ul className="space-y-3 mb-6">
                {[
                  'Unlimited customers & jobs',
                  'Invoicing & online payments',
                  'Expense tracking',
                  'SuddySnap™ receipt scanner',
                  'Schedule C tax reports',
                  'Essential email notifications'
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Link 
                to="/register" 
                className="block w-full text-center py-3 border-2 border-blue-600 text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Start Free Trial
              </Link>
            </div>

            {/* Pro Plan */}
            <div className="bg-gradient-to-b from-green-50 to-white rounded-xl border-2 border-green-400 p-6 shadow-lg relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-green-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                  MOST POPULAR
                </span>
              </div>
              
              <div className="text-center mb-6 mt-2">
                <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold mb-3">
                  PRO
                </span>
                <div className="text-4xl font-bold text-gray-900">$49<span className="text-lg font-normal text-gray-500">/mo</span></div>
                <p className="text-gray-600 mt-2">For growing teams</p>
              </div>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-sm">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Everything in Solo, plus:</strong></span>
                </li>
                {[
                  'Up to 15 team members',
                  'SuddyRoute™ optimization',
                  'Automated email reminders',
                  'Customer portal',
                  'P&L financial reports'
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Link 
                to="/register" 
                className="block w-full text-center py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                Start Free Trial
              </Link>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-purple-400 hover:shadow-lg transition-all">
              <div className="text-center mb-6">
                <span className="inline-block bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-semibold mb-3">
                  ENTERPRISE
                </span>
                <div className="text-4xl font-bold text-gray-900">$129<span className="text-lg font-normal text-gray-500">/mo</span></div>
                <p className="text-gray-600 mt-2">For serious operations</p>
              </div>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-sm">
                  <Check className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Everything in Pro, plus:</strong></span>
                </li>
                {[
                  'Unlimited team members',
                  'Custom branding',
                  'Marketing automation',
                  'API access',
                  'Multi-location support',
                  'Advanced analytics'
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <Check className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Link 
                to="/register" 
                className="block w-full text-center py-3 border-2 border-purple-600 text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
              >
                Start Free Trial
              </Link>
            </div>
          </div>

          <p className="text-center text-gray-500 mt-8">
            All plans include a 14-day free trial. No credit card required.
            <Link to="/pricing" className="text-blue-600 hover:underline ml-2">
              See full comparison →
            </Link>
          </p>
        </div>
      </section>

      {/* Field Service Industries */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Built for Field Service Professionals
            </h2>
            <p className="text-lg text-gray-600">
              If you drive to your customers, SuddyFlow is built for you
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {[
              '💦 Pressure Washing',
              '🪟 Window Cleaning',
              '🌳 Landscaping',
              '🧹 House Cleaning',
              '🏊 Pool Service',
              '❄️ HVAC',
              '🔧 Plumbing',
              '⚡ Electrical',
              '🛠️ Handyman',
              '🐛 Pest Control',
              '🌲 Tree Service',
              '🎄 Christmas Lights',
              '🧽 Carpet Cleaning',
              '🏠 Gutter Cleaning',
              '🪞 Glass & Mirror',
              '🚛 Junk Removal'
            ].map((industry) => (
              <span 
                key={industry}
                className="bg-white px-4 py-2 rounded-full text-sm font-medium text-gray-700 border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                {industry}
              </span>
            ))}
            <span className="bg-blue-100 px-4 py-2 rounded-full text-sm font-medium text-blue-700 border border-blue-200">
              + Any field service business
            </span>
          </div>
        </div>
      </section>

      {/* Social Proof / Trust */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="text-4xl font-bold text-blue-600 mb-2">500K+</div>
              <p className="text-gray-600">Field service businesses in the US alone</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-green-600 mb-2">50-70%</div>
              <p className="text-gray-600">Less expensive than leading competitors</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">45 min</div>
              <p className="text-gray-600">Average daily time saved with route optimization</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-blue-900/80 z-0" />

        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Simplify Your Business?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Join field service pros who've ditched the spreadsheets and streamlined their operations.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/register" 
              className="bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-700 shadow-lg transition-colors inline-flex items-center justify-center gap-2"
            >
              Start Your Free Trial
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="mt-6 text-blue-200 text-sm">
            14 days free • No credit card • Cancel anytime
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeaturesPage;